import Grid from '@/global/components/ui/Grid'
import { FormDatePicker } from 'global/components/Form'
import { SelectFuncionarios } from 'global/components/Selects'
import Dialog from 'global/guides/Dialog'
import { queryClient } from 'providers/ReactQuery'
import { useState } from 'react'
import { useMutation } from 'react-query'
import { centroCusto } from 'service/api'
import { toast } from 'sonner'
import { z } from 'zod'
import {
    ErroAssociacaoFuncionario,
    ErrosAssociacaoModal,
    FuncionarioAssociado,
} from './ErrosAssociacao'

const schema = z.object({
    funcionarioIds: z
        .array(z.number())
        .min(1, 'Selecione ao menos um funcionário'),
    dtInicial: z.any().refine(Boolean, 'Campo obrigatório'),
    dtFinal: z.any().nullish(),
})

type AssociarFuncionariosInput = {
    funcionarioIds: number[]
    dtInicial: string
    dtFinal?: string
}

type AssociarFuncionariosOutput = {
    associados: FuncionarioAssociado[]
    erros: ErroAssociacaoFuncionario[]
}

interface Props {
    centroCustoId: number
    /** Já vinculados: aparecem marcados e travados no select. */
    funcionarioIdsAssociados: number[]
    open: boolean
    onClose: () => void
}

/** Associa vários funcionários ao centro de custo de uma vez, com a mesma vigência. */
export function AdicionarFuncionariosCentroCusto({
    centroCustoId,
    funcionarioIdsAssociados,
    open,
    onClose,
}: Props) {
    const [associados, setAssociados] = useState<FuncionarioAssociado[]>([])
    const [erros, setErros] = useState<ErroAssociacaoFuncionario[]>([])
    const { mutateAsync, isLoading } = useMutation(
        (input: AssociarFuncionariosInput) =>
            centroCusto.patchFrom<
                AssociarFuncionariosInput,
                AssociarFuncionariosOutput
            >(centroCustoId, 'funcionarios', input),
    )

    function handleSubmit({
        funcionarioIds,
        dtInicial,
        dtFinal,
    }: z.infer<typeof schema>) {
        return mutateAsync(
            { funcionarioIds, dtInicial, dtFinal: dtFinal || undefined },
            {
                onSuccess: resposta => {
                    if (resposta.associados.length) {
                        toast.success(
                            `${resposta.associados.length} funcionário(s) associado(s) com sucesso`,
                        )
                        queryClient.invalidateQueries([
                            'centroCusto',
                            centroCustoId,
                        ])
                    }
                    setAssociados(resposta.associados)
                    setErros(resposta.erros)
                },
                onError: (error: any) => {
                    toast.error(
                        error?.message ?? 'Erro ao associar funcionário(s)',
                    )
                },
            },
        )
    }

    return [
        <Dialog.Root
            key="adicionar"
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            loading={isLoading}
            schema={schema}
        >
            <Dialog.Title>Associar funcionários</Dialog.Title>
            <Dialog.Content>
                <Grid container>
                    <Grid item xs={12}>
                        <SelectFuncionarios
                            form
                            checked={funcionarioIdsAssociados}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormDatePicker
                            name="dtInicial"
                            label="Data inicial"
                            type="date"
                            required
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormDatePicker
                            name="dtFinal"
                            label="Data final"
                            type="date"
                            helperText="Deixe em branco para indeterminado"
                        />
                    </Grid>
                </Grid>
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
                <Dialog.Actions.PrimaryButton closeOnSubmit />
            </Dialog.Actions>
        </Dialog.Root>,
        <ErrosAssociacaoModal
            key="erros"
            associados={associados}
            erros={erros}
            onClose={() => setErros([])}
        />,
    ]
}
